import { createFileRoute } from '@tanstack/react-router'

import { useState, useEffect } from "react";
import { useSession } from '@/lib/mocks/session';
import { useRouter } from '@/lib/mocks/router';
import { motion } from "framer-motion";
import {
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  ArrowRight,
  Loader2,
  UserCheck,
  Lock,
} from "lucide-react";
import { Button } from '@platform/ui';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@platform/ui';
import { Alert, AlertDescription } from '@platform/ui';
import { Progress } from '@platform/ui';
import { useTranslations } from '@/i18n/useTranslations';
import { BrandLogo } from '@/modules/shared/BrandLogo';
import { SecurityQuestionsCard } from '@/modules/account-security/SecurityQuestionsCard';
import { demoActions } from '@/lib/demo-store';
import { features } from '@/lib/platform';

function KycRequiredPage() {
  const t = useTranslations("KYC");
  const { data: session, status } = useSession();
  const router = useRouter();
  const [kycStatus, setKycStatus] = useState<string>("PENDING");
  const [isLoading, setIsLoading] = useState(true);
  const [isStartingKyc, setIsStartingKyc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kycUrl, setKycUrl] = useState<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    if (!session) {
      if (features.demoMode) {
        demoActions.login('demo@platform.app');
        return;
      }
      router.push("/");
      return;
    }

    checkKycStatus();

    const interval = setInterval(() => {
      if (kycStatus === "IN_PROGRESS") {
        checkKycStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [session, status, kycStatus]);

  const checkKycStatus = async () => {
    try {
      const response = await fetch("/api/kyc/check-status");
      if (response.ok) {
        const data = await response.json();
        setKycStatus(data.kycStatus || "PENDING");

        if (data.kycStatus === "VERIFIED") {
          router.push("/dashboard/user");
        }
      }
    } catch (error) {
      console.error("Error checking KYC status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startKycVerification = async () => {
    setIsStartingKyc(true);
    setError(null);

    try {
      const response = await fetch("/api/kyc/init", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setKycUrl(data.url);
        setKycStatus("IN_PROGRESS");
        window.open(data.url, "_blank");
      } else {
        const errorData = await response.json();
        setError(errorData.error || t("error_starting_verification"));
      }
    } catch (error) {
      console.error("Error starting KYC:", error);
      setError(t("error_starting_verification"));
    } finally {
      setIsStartingKyc(false);
    }
  };

  const recoverKycLink = async () => {
    setIsStartingKyc(true);
    setError(null);
    
    try {
      const response = await fetch("/api/kyc/get-url");
      if (response.ok) {
        const data = await response.json();
        console.log("KYC URL response:", data);
        
        if (data.url) {
          const newWindow = window.open(data.url, "_blank");
          
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            setError(t("error_popup_blocked"));
            setKycUrl(data.url);
          }
          return;
        }
      }
      
      console.log("No URL found, calling /api/kyc/init...");
      const initResponse = await fetch("/api/kyc/init", {
        method: "POST",
      });
      
      if (initResponse.ok) {
        const initData = await initResponse.json();
        console.log("KYC Init response:", initData);
        
        if (initData.url) {
          setKycUrl(initData.url);
          const newWindow = window.open(initData.url, "_blank");
          
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            setError(t("error_popup_blocked_direct"));
          }
        } else {
          setError(t("error_no_link"));
        }
      } else {
        const errorData = await initResponse.json();
        setError(errorData.error || t("error_recover_session"));
      }
    } catch (error) {
      console.error("Error recovering KYC link:", error);
      setError(t("error_recover_link"));
    } finally {
      setIsStartingKyc(false);
    }
  };

  if (status === "loading" || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const steps = [
    { id: 1, title: t("step_registration"), completed: true },
    { id: 2, title: t("step_email_verified"), completed: !!session?.user?.email },
    { id: 3, title: t("step_kyc"), completed: kycStatus === "VERIFIED", current: kycStatus !== "VERIFIED" },
    { id: 4, title: t("step_dashboard"), completed: false },
  ];

  const progress = steps.filter(s => s.completed).length / steps.length * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="border-b border-border/50 backdrop-blur-lg bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
          <BrandLogo variant="primary" textClassName="text-xl font-semibold tracking-tight" />
          <div className="text-sm text-muted-foreground">
            {session?.user?.email}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Progress Steps */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-primary" />
                  {t("verification_progress")}
                </CardTitle>
                <CardDescription>
                  {t("complete_all_steps")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2">
                    {progress.toFixed(0)}% {t("completed")}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className={`text-center p-3 rounded-lg transition-all ${
                        step.completed
                          ? "bg-green-500/10 border border-green-500/20"
                          : step.current
                          ? "bg-primary/10 border border-primary/20"
                          : "bg-muted/50 border border-border/50"
                      }`}
                    >
                      <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${
                        step.completed
                          ? "bg-green-500/20 text-green-500"
                          : step.current
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}>
                        {step.completed ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : step.current ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                      </div>
                      <p className={`text-xs ${
                        step.completed ? "text-green-400" : step.current ? "text-primary" : "text-muted-foreground"
                      }`}>
                        {step.title}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Preguntas de seguridad (configurables ya en el onboarding) */}
            <div className="mb-8">
              <SecurityQuestionsCard />
            </div>

            {/* KYC Card */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">{t("title")}</CardTitle>
                <CardDescription>
                  {t("description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {error && (
                  <Alert className="border-red-500/50 bg-red-500/10">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <AlertDescription className="text-red-400">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Status Display */}
                <div className={`p-4 rounded-lg border ${
                  kycStatus === "VERIFIED" 
                    ? "border-green-500/50 bg-green-500/10" 
                    : kycStatus === "IN_PROGRESS"
                    ? "border-yellow-500/50 bg-yellow-500/10"
                    : kycStatus === "REJECTED"
                    ? "border-red-500/50 bg-red-500/10"
                    : "border-border/50 bg-muted/50"
                }`}>
                  <div className="flex items-center gap-3">
                    {kycStatus === "VERIFIED" && <CheckCircle className="w-5 h-5 text-green-500" />}
                    {kycStatus === "IN_PROGRESS" && <Clock className="w-5 h-5 text-yellow-500 animate-pulse" />}
                    {kycStatus === "REJECTED" && <AlertCircle className="w-5 h-5 text-red-500" />}
                    {kycStatus === "PENDING" && <Shield className="w-5 h-5 text-muted-foreground" />}
                    
                    <div>
                      <p className={`font-medium ${
                        kycStatus === "VERIFIED" ? "text-green-400"
                        : kycStatus === "IN_PROGRESS" ? "text-yellow-400"
                        : kycStatus === "REJECTED" ? "text-red-400"
                        : "text-foreground"
                      }`}>
                        {kycStatus === "VERIFIED" && t("status_title_verified")}
                        {kycStatus === "IN_PROGRESS" && t("status_title_in_progress")}
                        {kycStatus === "REJECTED" && t("status_title_rejected")}
                        {kycStatus === "PENDING" && t("status_title_pending")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {kycStatus === "VERIFIED" && t("status_desc_verified")}
                        {kycStatus === "IN_PROGRESS" && t("status_desc_in_progress")}
                        {kycStatus === "REJECTED" && t("status_desc_rejected")}
                        {kycStatus === "PENDING" && t("status_desc_pending")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {kycStatus === "PENDING" && (
                    <Button
                      className="w-full button-gradient py-6 text-lg"
                      onClick={startKycVerification}
                      disabled={isStartingKyc}
                    >
                      {isStartingKyc ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t("starting_verification")}
                        </>
                      ) : (
                        <>
                          <Shield className="w-5 h-5 mr-2" />
                          {t("start_kyc_verification")}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  )}

                  {kycStatus === "IN_PROGRESS" && (
                    <>
                      <Button
                        variant="outline"
                        className="w-full py-6 text-lg"
                        onClick={recoverKycLink}
                        disabled={isStartingKyc}
                      >
                        {isStartingKyc ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {t("opening")}
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-5 h-5 mr-2" />
                            {t("open_verification_window")}
                          </>
                        )}
                      </Button>
                      
                      {kycUrl && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-2">
                            {t("direct_link_help")}
                          </p>
                          <a 
                            href={kycUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline text-sm flex items-center justify-center gap-1"
                          >
                            {t("go_to_verification")}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      
                      <p className="text-center text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                        {t("checking_status_auto")}
                      </p>
                    </>
                  )}

                  {kycStatus === "REJECTED" && (
                    <Button
                      className="w-full button-gradient py-6 text-lg"
                      onClick={startKycVerification}
                      disabled={isStartingKyc}
                    >
                      {isStartingKyc ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t("starting")}
                        </>
                      ) : (
                        <>
                          <Shield className="w-5 h-5 mr-2" />
                          {t("try_again")}
                        </>
                      )}
                    </Button>
                  )}

                  {kycStatus === "VERIFIED" && (
                    <Button
                      className="w-full button-gradient py-6 text-lg"
                      onClick={() => router.push("/dashboard/user")}
                    >
                      {t("go_to_dashboard")}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-sm">{t("what_you_need")}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-primary" />
                      {t("requirement_id")}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-primary" />
                      {t("requirement_camera")}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-primary" />
                      {t("requirement_lighting")}
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-4">
        <div className="container mx-auto max-w-7xl px-4 text-center text-sm text-muted-foreground">
          <p>{t("security_notice")}</p>
        </div>
      </footer>
    </div>
  );
}

export const Route = createFileRoute('/kyc-required')({
  component: KycRequiredPage,
})
